import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, GraduationCap } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  React.useEffect(() => { if (user) navigate("/dashboard"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (e2) {
      const status = e2.response?.status;
      if (!e2.response) setErr("Can't reach the server. Please check your internet connection and try again.");
      else if (status === 429) setErr("Too many attempts. Please wait a few minutes, then try again.");
      else setErr(formatApiError(e2.response?.data?.detail) || "Login failed. Please re-check your email and password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-gradient-to-br from-blue-700 to-indigo-900 text-white p-12">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-white/15 grid place-items-center font-head font-extrabold text-xl">L</div>
          <span className="font-head font-extrabold text-2xl">LaunchX Academy</span>
        </div>
        <div>
          <h1 className="font-head text-4xl font-extrabold leading-tight">Admissions & Lead CRM<br />built for Rampur.</h1>
          <p className="mt-4 text-blue-100 text-lg max-w-md">Capture leads once, assign ownership, surface the next action, and track admissions & money — accurately.</p>
          <div className="mt-8 flex gap-2 flex-wrap">
            {["Digital Marketing", "Meta Ads", "Shopify", "Marketplace Selling"].map((c) => (
              <span key={c} className="text-sm bg-white/10 px-3 py-1.5 rounded-full">{c}</span>
            ))}
          </div>
        </div>
        <p className="text-blue-200 text-sm">Practical online business education • Uttar Pradesh</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <GraduationCap className="w-7 h-7 text-brand-blue" />
            <span className="font-head font-extrabold text-xl">LaunchX Academy</span>
          </div>
          <h2 className="font-head text-2xl font-bold mb-1">Sign in</h2>
          <p className="text-sm text-slate-500 mb-6">Use your staff account to continue.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="username" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
            </div>
            {err && <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950/40 rounded-lg px-3 py-2" data-testid="login-error">{err}</div>}
            <Button type="submit" disabled={loading} className="w-full bg-brand-blue hover:bg-blue-700" data-testid="login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
            <p className="text-xs text-slate-400 text-center pt-1" data-testid="login-reset-note">
              Forgot your password? Ask your admin to reset it for you.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
